import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { PageHeader } from "../../components/bits";

const W = 180, H = 44, GX = 240, GY = 64;

export default function DependencyMapPage() {
  const { projectId } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/projects/${projectId}/schedule`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [projectId]);

  const layout = useMemo(() => {
    if (!data) return null;
    const deps = data.dependencies || [];
    const ids = new Set();
    deps.forEach((d) => { ids.add(d.fromTaskId); ids.add(d.toTaskId); });
    const tasks = (data.tasks || []).filter((t) => ids.has(t.id));
    if (tasks.length === 0) return { nodes: [], edges: [], width: 0, height: 0 };
    const preds = {};
    deps.forEach((d) => (preds[d.toTaskId] = [...(preds[d.toTaskId] || []), d.fromTaskId]));
    const layer = {};
    const depth = (id, seen = new Set()) => {
      if (layer[id] !== undefined) return layer[id];
      if (seen.has(id)) return 0;
      seen.add(id);
      const ps = (preds[id] || []).filter((p) => ids.has(p));
      layer[id] = ps.length ? Math.max(...ps.map((p) => depth(p, seen))) + 1 : 0;
      return layer[id];
    };
    tasks.forEach((t) => depth(t.id));
    const cols = {};
    tasks.forEach((t) => (cols[layer[t.id]] = [...(cols[layer[t.id]] || []), t]));
    const critical = new Set(data.criticalIds || []);
    const pos = {};
    Object.entries(cols).forEach(([l, list]) => {
      list.sort((a, b) => String(a.wbsCode || "").localeCompare(String(b.wbsCode || "")));
      list.forEach((t, i) => { pos[t.id] = { x: 30 + Number(l) * GX, y: 30 + i * GY }; });
    });
    const nodes = tasks.map((t) => ({ ...t, ...pos[t.id], critical: critical.has(t.id) }));
    const edges = deps.filter((d) => pos[d.fromTaskId] && pos[d.toTaskId]).map((d) => ({
      ...d, critical: critical.has(d.fromTaskId) && critical.has(d.toTaskId),
      x1: pos[d.fromTaskId].x + W, y1: pos[d.fromTaskId].y + H / 2,
      x2: pos[d.toTaskId].x, y2: pos[d.toTaskId].y + H / 2,
    }));
    const width = 60 + (Math.max(...Object.keys(cols).map(Number)) + 1) * GX;
    const height = 60 + Math.max(...Object.values(cols).map((c) => c.length)) * GY;
    return { nodes, edges, width, height };
  }, [data]);

  if (!layout) return <div className="text-muted-foreground">Loading dependency network…</div>;

  return (
    <div data-testid="dependency-map-page">
      <PageHeader overline="Plan · Task network" title="Dependency Map">
        <div className="text-xs text-muted-foreground mt-1">
          Network view of linked tasks, layered by dependency depth. <span className="text-red-600 font-semibold">Red</span> = critical path. Manage links on the Gantt.
        </div>
      </PageHeader>
      {layout.nodes.length === 0 ? (
        <div className="border border-dashed border-border p-12 text-center text-muted-foreground text-sm mt-3">No dependencies yet — link tasks on the WBS / Gantt page to see the network.</div>
      ) : (
        <div className="border border-border bg-white overflow-auto mt-3" style={{ maxHeight: "calc(100vh - 220px)" }}>
          <svg data-testid="dependency-canvas" width={layout.width} height={layout.height}>
            <defs>
              <marker id="dep-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#737373" />
              </marker>
              <marker id="dep-arrow-crit" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#E02020" />
              </marker>
            </defs>
            {layout.edges.map((e, i) => {
              const mx = (e.x1 + e.x2) / 2;
              return (
                <g key={i} data-testid="dep-edge">
                  <path d={`M ${e.x1} ${e.y1} C ${mx} ${e.y1}, ${mx} ${e.y2}, ${e.x2 - 4} ${e.y2}`} fill="none"
                    stroke={e.critical ? "#E02020" : "#A3A3A3"} strokeWidth={e.critical ? 2 : 1.25}
                    markerEnd={`url(#${e.critical ? "dep-arrow-crit" : "dep-arrow"})`} />
                  {(e.lagDays || 0) !== 0 && <text x={mx} y={(e.y1 + e.y2) / 2 - 4} fontSize="9" fill="#CC8F00" textAnchor="middle">{e.type || "FS"}{e.lagDays > 0 ? `+${e.lagDays}` : e.lagDays}d</text>}
                </g>
              );
            })}
            {layout.nodes.map((n) => (
              <g key={n.id} data-testid={`dep-node${n.critical ? "-critical" : ""}`}>
                <rect x={n.x} y={n.y} width={W} height={H} fill={n.critical ? "#FEF2F2" : "#fff"}
                  stroke={n.critical ? "#E02020" : "#525252"} strokeWidth={n.critical ? 2 : 1.25} />
                <text x={n.x + 8} y={n.y + 18} fontSize="11" fontWeight="600" fill="#171717">
                  {(n.name || "").length > 26 ? n.name.slice(0, 25) + "…" : n.name}
                </text>
                <text x={n.x + 8} y={n.y + 33} fontSize="9" fill="#737373">
                  {n.wbsCode || ""} · {n.durationDays || 0}d{n.totalFloat != null ? ` · float ${n.totalFloat}d` : ""}
                </text>
              </g>
            ))}
          </svg>
        </div>
      )}
    </div>
  );
}
